import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getRoleResourceBindings, bulkUpdateResourceBindings } from '../services/iam';
import { resourcesApi } from '../services/api';
import { ResourceBinding } from '../types/iam';
import { AlertTriangle, RefreshCw, Save, Search, Server, X } from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
  PaginationState,
} from '@tanstack/react-table';
import { Button, Card, Checkbox, Chip, EmptyState, SearchField, PaginationBar, TanstackTable, Select, StatusBadge } from '@/components/ds-zta';
import { useUrlState } from '@/lib/useUrlState';
import { ROWS_PER_PAGE_OPTIONS, readPage, readRows } from '@/lib/urlPaging';
import { healthRank } from '@/lib/sortValues';

interface ResourceRow {
  id: string;
  external_id: string;
  name: string;
  type: string;
  endpoint: Record<string, any>;
  labels: Record<string, any>;
  metadata: Record<string, any>;
  health_status: string;
  last_seen: string;
  created_at: string;
  updated_at: string;
  isSelected: boolean;
  bindingId?: string;
}


const columnHelper = createColumnHelper<ResourceRow>();

interface Props {
  roleId: string;
}

const ResourceBindingsTab: React.FC<Props> = ({ roleId }) => {
  const queryClient = useQueryClient();
  // Search, type and paging live in the URL (design eval F24), prefixed so
  // they sit beside RoleDetailPage's own `tab`. Type goes to the server, so
  // changing it starts at page 1; /resources/by-role takes no search param,
  // so the search filters the loaded page and says so (F7).
  const [url, setUrl] = useUrlState({ rq: '', rtype: '', rpage: '1', rrows: '20' });
  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: readPage(url.rpage) - 1, pageSize: readRows(url.rrows) }),
    [url.rpage, url.rrows]
  );
  const setPagination = (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
    const next = typeof updater === 'function' ? updater(pagination) : updater;
    setUrl({ rpage: next.pageIndex + 1, rrows: next.pageSize });
  };
  const selectedType = url.rtype;
  const searchTerm = url.rq;
  const setSearchTerm = (next: string) => setUrl({ rq: next }, { replace: true });
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedResources, setSelectedResources] = useState<Set<string>>(new Set());
  const [originalBindings, setOriginalBindings] = useState<Set<string>>(new Set());

  // Fetch resources with pagination
  const { data: resourcesData, isLoading: isLoadingResources, isError: isResourcesError, refetch: refetchResources } = useQuery(
    ['resources', pagination.pageIndex, pagination.pageSize, selectedType],
    async () => {
      const response = await resourcesApi.listByRole(
        selectedType || undefined,
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize
      );
      return response.data;
    },
    { keepPreviousData: true }
  );

  // Fetch current resource bindings
  const { data: bindingsData, isLoading: isLoadingBindings } = useQuery(
    ['roleResourceBindings', roleId],
    () => getRoleResourceBindings(roleId)
  );

  const isLoading = isLoadingResources || isLoadingBindings;

  // Update bindings when data changes
  React.useEffect(() => {
    if (bindingsData?.data) {
      const bindings = new Set<string>();
      bindingsData.data.forEach((binding: ResourceBinding) => {
        bindings.add(binding.resource_id);
      });
      setSelectedResources(new Set(bindings));
      setOriginalBindings(new Set(bindings));
      setHasChanges(false);
    }
  }, [bindingsData]);

  const saveMutation = useMutation(
    async () => {
      // Calculate changes
      const toAdd: ResourceBinding[] = [];
      const toRemove: string[] = [];

      // Find removed bindings
      originalBindings.forEach(resourceId => {
        if (!selectedResources.has(resourceId)) {
          const binding = bindingsData?.data.find(b => b.resource_id === resourceId);
          if (binding) {
            toRemove.push(binding.id);
          }
        }
      });

      // Find added bindings
      selectedResources.forEach(resourceId => {
        if (!originalBindings.has(resourceId)) {
          toAdd.push({
            role_id: roleId,
            resource_id: resourceId,
            attributes: {},
          } as ResourceBinding);
        }
      });

      return bulkUpdateResourceBindings(roleId, { add: toAdd, remove: toRemove });
    },
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(['roleResourceBindings', roleId]);
        toast.success(`Changes saved: ${result.added} added, ${result.removed} removed`);
        setHasChanges(false);
      },
      onError: (err: any) => {
        const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message;
        toast.error(`Failed to save changes: ${errorMessage}`);
        console.error('Bulk update error:', err.response?.data);
      },
    }
  );

  const handleToggleResource = (resourceId: string) => {
    const newSelected = new Set(selectedResources);
    if (newSelected.has(resourceId)) {
      newSelected.delete(resourceId);
    } else {
      newSelected.add(resourceId);
    }
    setSelectedResources(newSelected);
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate();
  };

  const handleCancel = () => {
    setSelectedResources(new Set(originalBindings));
    setHasChanges(false);
  };

  // Filter and prepare table data
  const tableData: ResourceRow[] = useMemo(() => {
    const filteredResources = (resourcesData?.resources || []).filter(resource =>
      resource.external_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.type.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return filteredResources.map(resource => {
      const name = resource.external_id;  // Use external_id as name
      const isSelected = selectedResources.has(resource.id);
      return {
        ...resource,
        name,
        isSelected,
      };
    });
  }, [resourcesData?.resources, selectedResources, searchTerm]);

  const isAllPageSelected = useMemo(() => {
    return tableData.length > 0 && tableData.every(resource => selectedResources.has(resource.id));
  }, [tableData, selectedResources]);

  const handleSelectAll = (checked: boolean) => {
    const newSelected = new Set(selectedResources);
    tableData.forEach(resource => {
      if (checked) {
        newSelected.add(resource.id);
      } else {
        newSelected.delete(resource.id);
      }
    });
    setSelectedResources(newSelected);
    setHasChanges(true);
  };

  // Define columns
  const columns = useMemo<ColumnDef<ResourceRow, any>[]>(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        meta: { sortable: true, sortValue: (r) => (r.name || '').toLowerCase() },
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="text-dense font-medium text-[var(--d2d-text-primary)]">
              {row.original.name}
            </span>
          </div>
        ),
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        meta: { sortable: true },
        cell: ({ getValue }) => (
          <Chip size="sm" variant="neutral">
            {getValue()}
          </Chip>
        ),
      }),
      columnHelper.accessor('health_status', {
        header: 'Status',
        meta: { sortable: true, sortValue: (r) => healthRank(r.health_status) },
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <StatusBadge value={status}>
              {status}
            </StatusBadge>
          );
        },
      }),
      columnHelper.accessor('isSelected', {
        header: 'Access',
        meta: { sortable: true, sortValue: (r) => (r.isSelected ? 0 : 1) },
        cell: ({ row }) => (
          <div className="flex items-center">
            <Checkbox
              size="sm"
              checked={row.original.isSelected}
              onChange={() => handleToggleResource(row.original.id)}
              label={row.original.isSelected ? 'Selected' : 'Not Selected'}
            />
          </div>
        ),
      }),
    ],
    [handleToggleResource]
  );

  // Create table instance
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    pageCount: resourcesData ? Math.ceil(resourcesData.total / pagination.pageSize) : 0,
    state: { pagination },
    onPaginationChange: setPagination,
    manualPagination: true,
  });

  const trimmedSearch = searchTerm.trim();
  const emptyState = isResourcesError ? (
    <EmptyState
      icon={<AlertTriangle />}
      title="Couldn't load resources"
      description="Something went wrong while loading this list."
      action={
        <Button size="sm" variant="secondary" leadingIcon={<RefreshCw className="h-4 w-4" />} onClick={() => refetchResources()}>
          Retry
        </Button>
      }
    />
  ) : trimmedSearch ? (
    <EmptyState
      icon={<Search />}
      title={`No resources on this page match "${trimmedSearch}"`}
      description="The search covers this page. Try another page, or a different name or type."
    />
  ) : selectedType ? (
    <EmptyState
      icon={<Server />}
      title="No resources of this type"
      description="Choose another type, or All Types."
    />
  ) : (
    <EmptyState
      icon={<Server />}
      title="No resources yet"
      description="Resources appear here once they are registered, and can then be bound to this role."
    />
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="p-4 overflow-visible">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <SearchField
              label="Search resources on this page"
              placeholder="Search resources on this page..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value)}
              help={
                (resourcesData?.total ?? 0) > (resourcesData?.resources?.length ?? 0)
                  ? `Searches the ${resourcesData?.resources?.length ?? 0} shown on this page, of ${resourcesData?.total ?? 0}.`
                  : undefined
              }
            />
          </div>
          <div className="sm:w-48">
            <Select
              className="gap-0" /* hidden label: no gap above the field, so it lines up with the search (F46) */
              label={<span className="sr-only">Resource type</span>}
              value={selectedType}
              onChange={(e) => setUrl({ rtype: e.target.value, rpage: null })}
            >
              <option value="">All Types</option>
              <option value="database">Database</option>
              <option value="http_proxy">HTTP Proxy</option>
              <option value="ssh">SSH Server</option>
              <option value="k8s">Kubernetes</option>
            </Select>
          </div>
        </div>
      </Card>
      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={saveMutation.isLoading}
            leadingIcon={<X className="h-4 w-4" />}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saveMutation.isLoading}
            leadingIcon={<Save className="h-4 w-4" />}
          >
            {saveMutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      )}

      {/* Resources Table */}
      <div className="bg-[var(--d2d-bg-card)] shadow-sm rounded-lg border border-[var(--d2d-border)] overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--d2d-border)]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-[var(--d2d-text-primary)]">Resources</h2>
              <p className="text-sm text-[var(--d2d-text-muted)] mt-1">
                {selectedResources.size} resource(s) with access
                {hasChanges && <span className="ml-2 text-[var(--d2d-warning)]">• Unsaved changes</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleSelectAll(!isAllPageSelected)}
              >
                {isAllPageSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <TanstackTable aria-label="Resources bound to this role" table={table} loading={isLoading} empty={emptyState} />
        </div>
      </div>

      {/* Pagination */}
      {resourcesData && resourcesData.total > 0 && (
          (resourcesData.total > 0 && <PaginationBar
            page={pagination.pageIndex + 1}
            totalRows={resourcesData.total}
            rowsPerPage={pagination.pageSize}
            onPageChange={(p) => setUrl({ rpage: p })}
            onRowsPerPageChange={(newItemsPerPage) => setUrl({ rrows: newItemsPerPage, rpage: null })}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />)
      )}
    </div>
  );
};

export default ResourceBindingsTab;
